import React from 'react';
import styled from '@emotion/styled';
import { graphql, PageProps } from 'gatsby';
import SEO from '@components/SEO';
import { PageContext, IPrismicClub } from '@appTypes';
import Separator from '@components/Separator';

// New Club-specific sections
import ClubHeroSection from '@pageSections/clubPage/hero';
import ClubJourneySection from '@pageSections/clubPage/journey';
import BenefitsSection from '@pageSections/traineesPage/benefits';
import ClubB2BSection from '@pageSections/clubPage/b2b';

// Reusing existing sections
import FAQSection from '@pageSections/traineesPage/faq';
import TraineesHowItWorksSection from '@pageSections/traineesPage/how-it-works';
import FooterSection from '@pageSections/common/footer';
import CallToActionSection from '@pageSections/common/callToAction';
import StickyMobileCTA from '@components/StickyMobileCTA';

import { Helmet } from 'react-helmet';

function removeTags(htmlString: string) {
  if (htmlString === null || htmlString === '') return '';
  else htmlString = htmlString.toString();
  return htmlString.replace(/(<([^>]+)>)/gi, '');
}

const ClubPage: React.FC<PageProps<IPrismicClub, PageContext>> = ({
  data: { prismicData, prismicFooterData, prismicCtaData },
  pageContext,
  location,
}) => {
  const { locale } = pageContext;

  // Add defensive check similar to about-us page
  if (!prismicData?.edges?.[0]?.node?.data) {
    return null;
  }

  const data = prismicData.edges[0].node.data;

  // Destructure data following existing patterns
  const {
    meta_title,
    meta_description,
    meta_image,
    meta_url,
    faq_items,
    header_title,
    header_subtitle,
    app_screenshot,
    ios_app_link,
    android_app_link,
    ios_app_id,
    android_app_id,
    journey_title,
    journey_pillars,
    journey_mockups,
    how_it_works_title,
    how_it_works_subtitle,
    how_it_works_steps,
    mobile_frame,
    benefits_title,
    benefits_subtitle,
    benefits,
    b2b_title,
    b2b_description,
    b2b_points,
    b2b_cta_text,
    b2b_button_link,
    b2b_mockup,
    faq_title,
    faq_subtitle
  } = data;

  // FAQ Schema generation (following existing pattern)
  const faqSchemaOrgJSONLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faq_items?.map((item: any) => ({
      '@type': 'Question',
      'name': item.faq_question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.faq_answer.html,
      },
    })) || [],
  };

  // More defensive data transformations
  const transformedHowItWorksTitle = how_it_works_title?.html
    ? { text: removeTags(how_it_works_title.html) }
    : { text: '' };

  const transformedHowItWorksSubtitle = how_it_works_subtitle?.html
    ? { text: removeTags(how_it_works_subtitle.html) }
    : { text: '' };

  return (
    <>
      <SEO
        title={meta_title}
        description={meta_description}
        image={meta_image?.url}
        url={meta_url}
        pathname={location.pathname}
        name='Hyperhuman'
        locale={locale}
      />
      <Helmet>
        <script type='application/ld+json'>{JSON.stringify(faqSchemaOrgJSONLD)}</script>
      </Helmet>

      <Container>
        {/* Only render ClubHeroSection when we have the required data */}
        {header_title && (
          <ClubHeroSection
            {...data}
            b2b_demo_anchor="#b2b-section"
          />
        )}

        {journey_title && journey_pillars && (
          <ClubJourneySection
            journey_title={journey_title}
            journey_pillars={journey_pillars}
            journey_mockups={journey_mockups}
          />
        )}

        <Separator />
        {how_it_works_title && (
          <TraineesHowItWorksSection
            how_it_works_title={transformedHowItWorksTitle}
            how_it_works_subtitle={transformedHowItWorksSubtitle}
            how_it_works_steps={how_it_works_steps}
            mobile_frame={mobile_frame}
          />
        )}

        <Separator />
        {benefits_title && (
          <BenefitsSection
            benefits_title={{ text: removeTags(benefits_title?.html || '') }}
            benefits_subtitle={{ text: removeTags(benefits_subtitle?.html || '') }}
            benefits={benefits || []}
          />
        )}

        <Separator />
        {b2b_title && (
          <ClubB2BSection
            b2b_title={b2b_title}
            b2b_description={b2b_description}
            b2b_points={b2b_points}
            b2b_cta_text={b2b_cta_text}
            b2b_button_link={b2b_button_link}
            b2b_mockup={b2b_mockup}
          />
        )}

        <Separator />
        {faq_title && (
          <FAQSection
            faq_title={faq_title}
            faq_subtitle={faq_subtitle}
            faq_items={faq_items}
          />
        )}

        <CallToActionSection prismicCtaData={prismicCtaData} />
        <FooterSection prismicFooterData={prismicFooterData} subscribeForm />
      </Container>

      {/* Sticky Mobile CTA - only show when we have app links */}
      {(ios_app_link || android_app_link) && (
        <StickyMobileCTA
          ios_app_link={ios_app_link}
          android_app_link={android_app_link}
          ios_app_id={ios_app_id}
          android_app_id={android_app_id}
        />
      )}
    </>
  );
};

export default ClubPage;

const Container = styled.div`
  position: initial;
`;

export const pageQuery = graphql`
  query ClubQuery($locale: String!) {
    prismicData: allPrismicClub(filter: { lang: { eq: $locale } }) {
      edges {
        node {
          lang
          data {
            # Metadata
            meta_title
            meta_image {
              url
              alt
            }
            meta_description
            meta_keywords
            meta_url
            
            # Header/Hero Section
            header_title {
              text
            }
            header_subtitle {
              text
            }
            app_screenshot {
              url
              alt
            }
            ios_app_link
            ios_app_id
            android_app_id
            android_app_link
            
            # How it Works Section
            how_it_works_title {
              html
            }
            how_it_works_subtitle {
              html
            }
            how_it_works_steps {
              step_image {
                url
                alt
              }
              video_id
              step_label
              step_title
              step_subtitle
              intermediate_step
            }
            mobile_frame {
              url
              alt
            }
            
            # Benefits Section
            benefits_title {
              html
            }
            benefits_subtitle {
              html
            }
            benefits {
              benefit_icon {
                url
                alt
              }
              benefit_title
              benefit_subtitle
            }
            
            # Journey Section
            journey_title
            journey_pillars {
              pillar_icon {
                url
                alt
              }
              pillar_title
              pillar_description
            }
            journey_mockups {
              mockup_image {
                url
                alt
              }
              mockup_label
            }
            
            # B2B Section
            b2b_title
            b2b_description
            b2b_points {
              point_text
            }
            b2b_cta_text
            b2b_button_link
            b2b_mockup {
              url
              alt
            }
            
            # FAQ Section
            faq_title
            faq_subtitle
            faq_items {
              faq_question
              faq_answer {
                html
              }
            }
          }
        }
      }
    }
    prismicFooterData: allPrismicIndex(filter: { lang: { eq: $locale } }) {
      ...FooterFieldsFragment
    }
    prismicCtaData: allPrismicIndex(filter: { lang: { eq: $locale } }) {
      ...CtaFieldsFragment
    }
  }
`;
